import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, MapPin, Mail, Phone, Lock } from 'lucide-react';
import SectionTitle from './SectionTitle';
import { useToast } from '../hooks/use-toast';

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', org: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      toast({
        title: 'TALEP İLETİLDİ',
        description: `${form.name || 'Anonim'}, başvurunuz şifreli kanaldan iletilmiştir. Yanıt 72 saat içindedir.`,
      });
      setForm({ name: '', org: '', email: '', message: '' });
      setSending(false);
    }, 1100);
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 overflow-hidden bg-[#070b12]">
      <div className="absolute inset-0 bg-grid opacity-30" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <SectionTitle
          code="SECTION / 07"
          title="ŞİFRELİ KANAL"
          subtitle="Savaş ve teknoloji ortaklıkları için güvenli iletişim."
        />

        <div className="mt-16 grid lg:grid-cols-12 gap-8">
          {/* Contact panel */}
          <div className="lg:col-span-5 space-y-4">
            {[
              { icon: MapPin, label: 'KOORDİNAT', value: 'Kocaeli · Gölcük Tersanesi' },
              { icon: Mail, label: 'GUVENLİ E-POSTA', value: 'utku@savunma.tr' },
              { icon: Phone, label: 'KOMUTANLIK', value: '+90 (262) 000 00 00' },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex items-center gap-4 p-5 border border-cyan-500/15 bg-[#0a0f17] group hover:border-cyan-400/40 transition-colors"
                >
                  <div className="w-10 h-10 border border-cyan-400/40 flex items-center justify-center text-cyan-300">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.25em] text-cyan-400/60">{c.label}</div>
                    <div className="text-white font-medium mt-0.5">{c.value}</div>
                  </div>
                </motion.div>
              );
            })}

            <div className="p-5 border border-cyan-500/15 bg-[#0a0f17] flex items-start gap-3">
              <Lock className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-slate-400 leading-relaxed">
                Tüm haberleşme <span className="text-cyan-300">uçtan uca şifreleme</span> ile güvence altındadır.
                Sadece yetkili kişi ve kurumlar yanıtlanmaktadır.
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-7 relative p-7 md:p-10 border border-cyan-500/20 bg-[#0a0f17]">
            <span className="corner-mark corner-tl" />
            <span className="corner-mark corner-tr" />
            <span className="corner-mark corner-bl" />
            <span className="corner-mark corner-br" />

            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[10px] font-mono tracking-[0.25em] text-cyan-400/60">FORM-001 / SECURE</div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-white mt-1">TALEP GÖNDER</h3>
              </div>
              <span className="text-[10px] font-mono text-cyan-300 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-400 blink" />
                CHANNEL OPEN
              </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="İSİM / SOYİSİM" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label="KURUM" value={form.org} onChange={(v) => setForm({ ...form, org: v })} />
              <div className="sm:col-span-2">
                <Field label="GUVENLİ E-POSTA" value={form.email} type="email" onChange={(v) => setForm({ ...form, email: v })} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-mono tracking-[0.25em] text-cyan-400/70 mb-2">MESAJ</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                  rows={5}
                  className="w-full bg-transparent border border-cyan-500/20 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors resize-none"
                  placeholder="Şifreli mesajınızı yazın..."
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 text-[#05080d] font-mono text-xs tracking-[0.25em] transition-colors"
            >
              {sending ? 'GÖNDERİLİYOR...' : 'GÜVENLİ GÖNDER'}
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-[10px] font-mono tracking-[0.25em] text-cyan-400/70 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full bg-transparent border border-cyan-500/20 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors"
      />
    </div>
  );
}
